import HeroSlider, { HeroSlideData } from '@/components/customer/hero-slider';
import SearchBar from '@/components/customer/search-bar';
import { Button } from '@/components/ui/button';
import BaseLayout from '@/layouts/baselayout';
import { pluralize } from '@/utils/plural';
import { router } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import Icon from '../../icons/icon';

const propertyTypes = [
    {
        name: 'Motels',
        img: '/assets/home/w8EfVnNBo40KKMjVHI144JHcNyKN0gbjIUaVXr3c.webp',
    },
    {
        name: 'Apartments',
        img: '/assets/home/5p29hFg13jN24C9iXIj4zw1H2FUQukgZRNZQ7WLx.webp',
    },
    {
        name: 'Resorts',
        img: '/assets/home/currysinn_1.png',
    },
    {
        name: 'Villas',
        img: '/assets/home/currysinn_2.png',
    },
    // {
    //     name: 'Guest Houses',
    //     img: 'https://st.hzcdn.com/simgs/a2319fbd0509c0e6_14-2347/home-design.jpg',
    // },
    // {
    //     name: 'Ryokans',
    //     img: 'https://images.squarespace-cdn.com/content/v1/62f1cb15a2cb083186ccd6d1/49702a8d-f890-4cad-a39f-954f453f5732/IMG_1940.JPG',
    // },
];

type TrendingItem = {
    city: string;
    properties?: number | null;
    comingSoon?: boolean;
    img: string;
};

// Will be loaded from API: GET /api/trending-destinations

interface IndexProps {
    heroSlides: HeroSlideData[];
}

export default function Index({ heroSlides }: IndexProps) {
    const trendingRef = useRef<HTMLDivElement | null>(null);
    const sliderRef = useRef<HTMLDivElement | null>(null);
    const [trendingDestinations, setTrendingDestinations] = useState<
        TrendingItem[]
    >([]);

    // Fetch trending destinations from API
    useEffect(() => {
        let mounted = true;

        fetch('/trending-destinations')
            .then((r) => r.json())
            .then((payload) => {
                if (!mounted) return;
                if (payload && payload.success && Array.isArray(payload.data)) {
                    setTrendingDestinations(payload.data as TrendingItem[]);
                }
            })
            .catch(() => {
                // keep static fallback if needed
            });

        return () => {
            mounted = false;
        };
    }, []);

    return (
        <BaseLayout>
            <div className="min-h-screen w-full bg-gray-100 text-gray-900">
                <HeroSlider slides={heroSlides} />

                {/* SEARCH BAR */}
                <div
                    className={`relative z-20 px-4 md:px-8 lg:px-16 ${heroSlides.length > 0 ? '-mt-7' : 'mt-5'}`}
                >
                    <SearchBar />
                </div>
                {/* PROPERTY TYPE */}
                <div className="relative mt-5 px-4 md:px-8 lg:px-16">
                    <h2 className="mb-4 text-xl font-bold sm:text-2xl lg:text-3xl">
                        Explore deals
                    </h2>

                    <div className="relative">
                        {/* LEFT ARROW */}
                        <Button
                            variant="bare"
                            onClick={() =>
                                sliderRef.current?.scrollBy({
                                    left: -300,
                                    behavior: 'smooth',
                                })
                            }
                            className="absolute top-1/2 left-0 z-20 hidden h-10 w-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-card shadow md:flex"
                        >
                            <Icon
                                iconName="ChevronLeft"
                                className="h-5 w-5 stroke-gray-700"
                            />
                        </Button>

                        {/* RIGHT ARROW */}
                        <Button
                            variant="bare"
                            onClick={() =>
                                sliderRef.current?.scrollBy({
                                    left: 300,
                                    behavior: 'smooth',
                                })
                            }
                            className="absolute top-1/2 right-0 z-20 hidden h-10 w-10 translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-card shadow md:flex"
                        >
                            <Icon
                                iconName="ChevronRight"
                                className="h-5 w-5 stroke-gray-700"
                            />
                        </Button>

                        {/* SCROLL SLIDER */}
                        <div
                            ref={sliderRef}
                            className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth"
                            style={{ scrollbarWidth: 'none' }}
                        >
                            {propertyTypes.map((item) => (
                                <Button
                                    variant="bare"
                                    key={item.name}
                                    // onClick={() => router.visit(`/motels?type=${encodeURIComponent(item.name)}`)}
                                    className="group relative aspect-[4/3] min-w-[75%] cursor-pointer snap-start overflow-hidden rounded-xl shadow-md sm:min-w-[40%] md:min-w-[25%]"
                                >
                                    <img
                                        src={item.img}
                                        alt={item.name}
                                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-black/20" />
                                    {/* <p className="absolute bottom-3 left-3 text-lg font-semibold text-white">
                                        {item.name}
                                    </p> */}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* TRENDING DESTINATIONS */}
                <div className="relative mt-6 px-4 pb-6 md:px-8 lg:px-16">
                    <div className="mb-4 flex flex-wrap items-baseline gap-2">
                        <h2 className="text-xl font-bold sm:text-2xl lg:text-3xl">
                            Trending destinations
                        </h2>
                        <p className="text-sm text-gray-600 sm:text-base">
                            • Top picks loved by travelers from the USA
                        </p>
                    </div>

                    {/* WRAPPER for arrows + slider */}
                    <div className="relative">
                        {/* LEFT ARROW */}
                        <Button
                            variant="bare"
                            onClick={() =>
                                trendingRef.current?.scrollBy({
                                    left: -300,
                                    behavior: 'smooth',
                                })
                            }
                            className="absolute top-1/2 left-0 z-20 hidden h-10 w-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-card shadow md:flex"
                        >
                            <Icon
                                iconName="ChevronLeft"
                                className="h-5 w-5 stroke-gray-700"
                            />
                        </Button>

                        {/* RIGHT ARROW */}
                        <Button
                            variant="bare"
                            onClick={() =>
                                trendingRef.current?.scrollBy({
                                    left: 300,
                                    behavior: 'smooth',
                                })
                            }
                            className="absolute top-1/2 right-0 z-20 hidden h-10 w-10 translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-card shadow md:flex"
                        >
                            <Icon
                                iconName="ChevronRight"
                                className="h-5 w-5 stroke-gray-700"
                            />
                        </Button>

                        {/* SLIDER */}
                        <div
                            ref={trendingRef}
                            className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth"
                            style={{ scrollbarWidth: 'none' }}
                        >
                            {trendingDestinations.map((item) => (
                                <Button
                                    variant="bare"
                                    key={item.city}
                                    onClick={() =>
                                        router.visit(
                                            `/motels?query=${encodeURIComponent(item.city)}`,
                                        )
                                    }
                                    className="group relative aspect-[4/3] min-w-[75%] cursor-pointer snap-start overflow-hidden rounded-xl shadow-sm sm:min-w-[40%] md:min-w-[25%]"
                                >
                                    <img
                                        src={item.img}
                                        alt={item.city}
                                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                    <span className="absolute bottom-10 left-3 text-lg font-semibold text-white">
                                        {item.city}
                                    </span>
                                    <span className="absolute bottom-3 left-3 text-sm text-gray-200">
                                        {item.comingSoon
                                            ? 'Coming Soon'
                                            : `${pluralize(item.properties ?? 0, 'property', 'properties')}`}
                                    </span>
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </BaseLayout>
    );
}
